import { computed, inject, onBeforeUnmount, onMounted, ref, useId } from "vue"
import { useFormContext } from "./useFormContext"
import { required as requiredValidator } from "../validators"
import { SectionIdInjectionKey, type FieldInfo } from "../types"

export interface UseFormFieldOptions {
  name: string
  required?: boolean
  validate?: (value: unknown) => string | null
  getValue?: () => unknown
  /** Optional override for dirty tracking — see FormField's `dirtyField` prop. */
  dirtyField?: string
  /** Sichtbare Beschriftung, landet im registrierten FieldInfo. */
  label?: string
}

export interface UseFormFieldResult extends FieldInfo {
  setTouched: (touched: boolean) => void
}

export function useFormField(options: UseFormFieldOptions): UseFormFieldResult {
  const api = useFormContext()
  const sectionId = inject(SectionIdInjectionKey, null)
  const id = useId()
  const touched = ref(false)
  const requiredFn = options.required ? requiredValidator() : null

  const error = computed<string | null>(() => {
    if (!touched.value) return null
    const formValue = api.value.value as Record<string, unknown>
    // Auto-read fallback: when getValue is not provided, look up `name` as a
    // TOP-LEVEL key on the form value. For nested or aliased fields (e.g.,
    // `localPlayerCountMin` mapped to `localPlayerCount.min`), the caller
    // MUST pass an explicit getValue, otherwise validators receive undefined.
    const v = options.getValue
      ? options.getValue()
      : (formValue && options.name in formValue ? formValue[options.name] : undefined)
    if (requiredFn) {
      const err = requiredFn(v)
      if (err) return err
    }
    if (options.validate) {
      const err = options.validate(v)
      if (err) return err
    }
    return api.serverErrors.value[options.name] ?? api.formErrors.value[options.name] ?? null
  })

  const dirty = computed<boolean>(() =>
    api.changedFieldNames.value.includes(options.dirtyField ?? options.name)
  )

  const info: FieldInfo = {
    id,
    name: options.name,
    required: !!options.required,
    error,
    touched,
    dirty,
    sectionId,
    label: options.label,
    dirtyField: options.dirtyField
  }

  onMounted(() => api.registerField(info))
  onBeforeUnmount(() => api.unregisterField(options.name))

  return {
    ...info,
    setTouched: (t) => { touched.value = t }
  }
}
